<script setup>
import axios from 'axios';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { isValidEmail } from '../../../helpers/helper';
import PgeButton from '../../../pge-components/pge-button.vue';
import PgeInputText from '../../../pge-components/pge-input-text.vue';
import PgeTextarea from '../../../pge-components/pge-textarea.vue';

const props = defineProps({
    visible: { type: Boolean, default: false },
    initialEmail: { type: String, default: '' },
    eventData: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['close']);
const router = useRouter();

const form = ref({ first_name: '', last_name: '', email: props.initialEmail || '', phone: '', company: '', notes: '' });
const isLoading = ref(false);
const errors = ref([]);
const submitted = ref(false);

watch(() => props.initialEmail, (v) => { form.value.email = v || ''; });
watch(() => props.visible, (v) => {
    if (v) { submitted.value = false; errors.value = []; }
});

const PHONE_DISALLOWED = /[^0-9+()\-.\s]/g;

function sanitizePhone(value) {
    const cleaned = String(value ?? '').replace(PHONE_DISALLOWED, '').replace(/^\s+/, '');
    const lead = cleaned.startsWith('+') ? '+' : '';
    return lead + cleaned.replace(/\+/g, '');
}

// pge-input-text holds its own copy of the value, so rewriting only the parent
// model leaves a rejected character on screen whenever the sanitized result is
// unchanged. Rewriting the element and re-dispatching input keeps both in step.
function filterPhoneInput(event) {
    const el = event.target;
    const incoming = event.data ?? event.dataTransfer?.getData('text') ?? '';
    if (!incoming) return;

    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const proposed = el.value.slice(0, start) + incoming + el.value.slice(end);
    const sanitized = sanitizePhone(proposed);
    if (sanitized === proposed) return;

    event.preventDefault();
    const caret = sanitizePhone(el.value.slice(0, start) + incoming).length;
    el.value = sanitized;
    el.setSelectionRange(caret, caret);
    el.dispatchEvent(new Event('input', { bubbles: true }));
}

function onPhoneUpdate(value) {
    form.value.phone = sanitizePhone(value);
}

const phoneDigits = computed(() => (form.value.phone || '').replace(/\D/g, '').length);
const phoneValid = computed(() => phoneDigits.value >= 7 && phoneDigits.value <= 15);
const showPhoneHint = computed(() => !!form.value.phone && !phoneValid.value);

function canSubmit() {
    return !!form.value.first_name && !!form.value.last_name
        && isValidEmail((form.value.email || '').trim()) && phoneValid.value;
}

async function submit() {
    if (!canSubmit() || isLoading.value) return;
    isLoading.value = true;
    errors.value = [];
    try {
        await axios.post('/api/access/requests', {
            first_name: form.value.first_name,
            last_name: form.value.last_name,
            email: form.value.email.trim(),
            phone: form.value.phone,
            company: form.value.company,
            notes: form.value.notes,
        });
        submitted.value = true;
    } catch (error) {
        const data = error?.response?.data;
        errors.value = data?.errors
            ? (Array.isArray(data.errors) ? data.errors : Object.values(data.errors).flat())
            : [data?.message || 'Could not submit your request. Please try again.'];
    } finally {
        isLoading.value = false;
    }
}

function close() { emit('close'); }

// Request submitted → send the user to the hub home, not back to login/register.
function goToHub() {
    emit('close');
    router.push('/');
}
</script>

<template>
    <div class="ra-overlay" v-if="visible" @click.self="close">
        <div class="ra-modal" :class="{ 'ra-modal--success': submitted }">
            <button class="ra-close" type="button" @click="close" aria-label="Close">×</button>

            <template v-if="!submitted">
                <img v-if="eventData?.settings?.logo" class="ra-logo" :src="eventData.settings.logo" alt="" />
                <h2 class="ra-title">Request Access</h2>
                <p class="ra-desc">Complete this form. Our team will review your request and contact you as soon as possible.</p>

                <div class="ra-row">
                    <pge-input-text type="text" name="first_name" label="First Name" :model="form.first_name" v-model="form.first_name" required has-required-sign />
                    <pge-input-text type="text" name="last_name" label="Last Name" :model="form.last_name" v-model="form.last_name" required has-required-sign />
                </div>
                <pge-input-text type="email" name="email" label="Email" :model="form.email" v-model="form.email" required has-required-sign />
                <pge-input-text type="text" name="phone" label="Phone" :model="form.phone" @update:modelValue="onPhoneUpdate" @beforeinput="filterPhoneInput" required has-required-sign />
                <div class="ra-hint" v-if="showPhoneHint">Enter a phone number with 7 to 15 digits.</div>
                <pge-input-text type="text" name="company" label="Company Name" :model="form.company" v-model="form.company" />
                <pge-textarea name="notes" label="Additional Notes" :model="form.notes" v-model="form.notes" :rows="3" />

                <div class="ra-errors" v-if="errors.length"><div v-for="e in errors" :key="e">{{ e }}</div></div>

                <div class="ra-actions">
                    <pge-button label="Close" template="text" @click="close" />
                    <pge-button label="Submit" @click="submit" :disabled="!canSubmit() || isLoading" />
                </div>
            </template>

            <template v-else>
                <div class="ra-check" aria-hidden="true">
                    <svg width="88" height="88" viewBox="0 0 88 88" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="44" cy="44" r="40" stroke="#54B15A" stroke-width="6" />
                        <path d="M28 45.5 L39 56.5 L61 32.5" stroke="#54B15A" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </div>
                <h2 class="ra-title ra-title--success">Request Submitted</h2>
                <p class="ra-desc">Thank you, {{ form.first_name }}. Your request has been received. We'll contact you at {{ form.email }} soon.</p>
                <p class="ra-desc ra-desc--muted">You'll receive a confirmation email shortly.</p>
                <div class="ra-actions ra-actions--center">
                    <pge-button label="Okay" @click="goToHub" />
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped>
/* Overlay itself scrolls (with padding) when the form is taller than the viewport,
   so the modal never grows an internal scrollbar and is never clipped. */
.ra-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; flex-direction: column; align-items: center; overflow-y: auto; padding: 32px 16px; }
.ra-modal { font-family: 'Lato', sans-serif; background: #fff; border-radius: 16px; padding: 32px; width: 100%; max-width: 460px; position: relative; display: flex; flex-direction: column; gap: 14px; margin: auto 0; }
.ra-modal--success { align-items: center; text-align: center; gap: 10px; padding: 44px 32px; }
.ra-logo { display: block; max-width: 72%; max-height: 76px; object-fit: contain; margin: 0 auto 4px; }
.ra-close { position: absolute; top: 12px; right: 16px; border: none; background: none; font-size: 24px; line-height: 1; cursor: pointer; color: #4B444D; }
.ra-title { font-family: 'Lato', sans-serif; text-align: center; font-weight: 700; font-size: 22px; color: #1E1A1F; margin: 0; }
.ra-title--success { font-size: 24px; margin-top: 4px; }
.ra-desc { font-family: 'Lato', sans-serif; font-size: 14px; line-height: 1.5; color: #49454F; text-align: center; margin: 0; }
.ra-desc--muted { color: #7D747E; margin-top: 4px; }
.ra-row { display: flex; gap: 15px; background-color: #fff; }
.ra-row > * { flex: 1; min-width: 0; }
.ra-actions { display: flex; justify-content: space-between; align-items: center; margin-top: 8px; }
.ra-actions--center { justify-content: center; margin-top: 12px; }
.ra-check { display: flex; justify-content: center; margin-bottom: 4px; }
.ra-errors { display: flex; flex-direction: column; gap: 6px; color: #f83c3c; font-weight: 700; font-size: 14px; text-align: center; }
.ra-hint { margin-top: -10px; color: #f83c3c; font-size: 12px; line-height: 1.4; }
</style>
